import { createReducer, on } from "@ngrx/store";
import { ScenarioCommand } from "src/components/shared-components/interfaces/iScenarios";
import {
  scenariosCommandDeletedAction,
  scenariosCommandsLoadErrorAction,
  scenariosCommandsLoadInitStartAction,
  scenariosCommandsLoadSuccessAction,
  scenariosCommandsUnsetKeyStorageAction,
} from "../actions/scenario-commands.action";
import { scenarioDeletedAction } from "../actions/scenario.action";

/**
 * Interface for a single entry of the scenario commands
 */
export interface ScenarioCommandsEntry {
  loaded: boolean;
  errorGeneric: boolean;
  data: ScenarioCommand[];
  errorCode: number;
}

/**
 * State for the scenario commands feature, indexed by scenario id
 */
export type ScenariosCommandsState = {
  [idScenario: number]: ScenarioCommandsEntry;
};

const initialState: ScenariosCommandsState = {};

const previousCommandsEntry = (
  previous: ScenarioCommandsEntry
): ScenarioCommandsEntry => ({
  loaded: false,
  errorGeneric: false,
  data: previous?.data ?? null,
  errorCode: -1,
});

export const ScenarioCommandsReducer = createReducer(
  initialState,
  on(scenariosCommandsUnsetKeyStorageAction, () => initialState),
  on(
    scenariosCommandsLoadInitStartAction,
    (state: ScenariosCommandsState, { idScenario }) => ({
      ...state,
      [idScenario]: previousCommandsEntry(state[idScenario]),
    })
  ),
  on(
    scenariosCommandsLoadSuccessAction,
    (state: ScenariosCommandsState, { idScenario, commands }) => ({
      ...state,
      [idScenario]: {
        loaded: true,
        errorGeneric: false,
        data: commands,
        errorCode: -1,
      },
    })
  ),
  on(
    scenariosCommandsLoadErrorAction,
    (state: ScenariosCommandsState, { idScenario, errorCode }) => ({
      ...state,
      [idScenario]: {
        loaded: true,
        errorGeneric: true,
        data: null,
        errorCode,
      },
    })
  ),
  // A single command was deleted by the acting client: remove it from the loaded
  // list in place, avoiding a full reload from API.
  on(
    scenariosCommandDeletedAction,
    (state: ScenariosCommandsState, { idScenario, idCommand }) => {
      const entry = state[idScenario];
      if (!entry?.data) {
        return state;
      }
      return {
        ...state,
        [idScenario]: {
          ...entry,
          data: entry.data.filter(
            (command: ScenarioCommand) => command.id !== idCommand
          ),
        },
      };
    }
  ),
  // When a scenario is removed (e.g. deleted from another client), drop its
  // commands entry too so no orphan/stale list is left in the store.
  on(
    scenarioDeletedAction,
    (state: ScenariosCommandsState, { idScenario }) => {
      if (!state[idScenario]) {
        return state;
      }
      const nextState = { ...state };
      delete nextState[idScenario];
      return nextState;
    }
  )
);
